import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp, API } from "../store";
import { Plus, Trash2, Edit, Save, Upload, Globe } from "lucide-react";

/* Admin Content Manager — edit translatable text/image/video across EN/PT/ES */
export function ContentManager() {
  const { authHeaders } = useApp();
  const [content, setContent] = useState({});
  const [editing, setEditing] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/content`).then((r) => setContent(r.data || {}));
  useEffect(() => { load(); }, []);

  const save = async (item) => {
    setSaving(true);
    for (const locale of ["en", "pt", "es"]) {
      const v = item.values?.[locale] || {};
      await axios.post(`${API}/admin/content`, { key: item.key, locale, ...v }, authHeaders());
    }
    setSaving(false); setEditing(null); load();
  };

  const del = async (key, locale) => {
    if (!confirm(`Excluir ${key} (${locale})?`)) return;
    await axios.delete(`${API}/admin/content/${encodeURIComponent(key)}/${locale}`, authHeaders());
    load();
  };

  const addNew = () => {
    if (!newKey.trim()) return;
    setEditing({
      key: newKey.trim(),
      values: { en: { value_text: "", value_image: "", value_video: "" }, pt: { value_text: "", value_image: "", value_video: "" }, es: { value_text: "", value_image: "", value_video: "" } },
    });
    setNewKey("");
  };

  return (
    <div data-testid="admin-content">
      <div className="overline">Gestão de Conteúdo</div>
      <h1 className="font-serif-display text-5xl text-slate-900">Textos & Mídia</h1>
      <p className="text-slate-500 text-sm mt-2 max-w-xl">Edite textos, imagens e vídeos customizados por idioma. Use chaves como <code className="bg-slate-100 px-1 rounded">home.hero.title</code> para sobrescrever conteúdo padrão.</p>

      <div className="mt-8 flex gap-2">
        <input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="Nova chave (ex: home.hero.title)" className="flex-1 bg-white border border-slate-300 px-3 py-2 rounded max-w-md font-ui text-sm" data-testid="content-new-key" />
        <button onClick={addNew} className="btn-primary" data-testid="content-add"><Plus size={14} /> Nova entrada</button>
      </div>

      <div className="mt-8 space-y-3">
        {Object.keys(content).length === 0 && <div className="text-slate-400 text-sm">Nenhum conteúdo customizado ainda.</div>}
        {Object.entries(content).map(([key, vals]) => (
          <div key={key} className="bg-white border border-slate-200 rounded-md p-5" data-testid={`content-row-${key}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <code className="text-sm text-slate-900 font-mono">{key}</code>
              </div>
              <button
                onClick={() => setEditing({
                  key,
                  values: {
                    en: { value_text: vals.en?.text || "", value_image: vals.en?.image || "", value_video: vals.en?.video || "" },
                    pt: { value_text: vals.pt?.text || "", value_image: vals.pt?.image || "", value_video: vals.pt?.video || "" },
                    es: { value_text: vals.es?.text || "", value_image: vals.es?.image || "", value_video: vals.es?.video || "" },
                  },
                })}
                className="text-slate-600 hover:text-sky-500 text-sm flex items-center gap-1" data-testid={`content-edit-${key}`}><Edit size={14} /> Editar</button>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              {["pt", "en", "es"].map((l) => (
                <div key={l} className="bg-slate-50 p-3 rounded border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="overline">{l.toUpperCase()}</span>
                    {vals[l] && <button onClick={() => del(key, l)} className="text-red-500 hover:text-red-600"><Trash2 size={12} /></button>}
                  </div>
                  <div className="text-slate-700 truncate">{vals[l]?.text || vals[l]?.image || vals[l]?.video || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-6 overflow-y-auto">
          <div className="bg-white rounded-md max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif-display text-3xl mb-6 text-slate-900">{editing.key}</h3>
            <div className="space-y-6">
              {["pt", "en", "es"].map((l) => (
                <div key={l} className="border border-slate-200 rounded p-4">
                  <div className="overline mb-3">{l.toUpperCase()}</div>
                  <label className="block mb-3">
                    <div className="text-xs text-slate-500 mb-1">Texto</div>
                    <textarea value={editing.values[l].value_text || ""} onChange={(e) => setEditing({ ...editing, values: { ...editing.values, [l]: { ...editing.values[l], value_text: e.target.value } } })}
                      rows={2} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm" data-testid={`content-text-${l}`} />
                  </label>
                  <label className="block mb-3">
                    <div className="text-xs text-slate-500 mb-1">URL de imagem</div>
                    <input value={editing.values[l].value_image || ""} onChange={(e) => setEditing({ ...editing, values: { ...editing.values, [l]: { ...editing.values[l], value_image: e.target.value } } })}
                      className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm" data-testid={`content-image-${l}`} placeholder="https://..." />
                  </label>
                  <label className="block">
                    <div className="text-xs text-slate-500 mb-1">URL de vídeo</div>
                    <input value={editing.values[l].value_video || ""} onChange={(e) => setEditing({ ...editing, values: { ...editing.values, [l]: { ...editing.values[l], value_video: e.target.value } } })}
                      className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm" data-testid={`content-video-${l}`} placeholder="https://youtube.com/..." />
                  </label>
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button>
              <button onClick={() => save(editing)} disabled={saving} className="btn-primary" data-testid="content-save"><Save size={14} /> {saving ? "Salvando…" : "Salvar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Blog manager */
const BLOG_STATUS_ORDER = {
  pending_approval: 0,
  approved: 1,
  published: 2,
};

const BLOG_STATUS_LABELS = {
  pending_approval: "Pendente aprovação",
  approved: "Aprovado",
  published: "Publicado",
};

function sortBlogPosts(items = []) {
  return [...items].sort((a, b) => {
    const rank = (item) => BLOG_STATUS_ORDER[item?.status || "published"] ?? 99;
    const byStatus = rank(a) - rank(b);
    if (byStatus !== 0) return byStatus;
    return new Date(b?.created_at || 0) - new Date(a?.created_at || 0);
  });
}

function BlogStatusBadge({ status }) {
  const label = BLOG_STATUS_LABELS[status || "published"] || (status || "Publicado");
  const cls = status === "pending_approval"
    ? "bg-amber-100 text-amber-800 border-amber-200"
    : status === "approved"
      ? "bg-sky-100 text-sky-800 border-sky-200"
      : "bg-emerald-100 text-emerald-800 border-emerald-200";
  return <span className={`inline-flex items-center rounded-full border px-2 py-1 text-[11px] font-semibold ${cls}`}>{label}</span>;
}

export function BlogManager() {
  const { authHeaders } = useApp();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [localized, setLocalized] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [rowActionLoading, setRowActionLoading] = useState("");

  const load = () => axios.get(`${API}/admin/blog`, authHeaders()).then((r) => setPosts(sortBlogPosts(r.data || [])));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Excluir post?")) return;
    await axios.delete(`${API}/admin/blog/${id}`, authHeaders());
    load();
  };

  const runRowAction = async (id, action) => {
    if (!id) return;
    setRowActionLoading(`${id}:${action}`);
    try {
      await axios.post(`${API}/admin/blog/${id}/${action}`, {}, authHeaders());
      load();
    } finally {
      setRowActionLoading("");
    }
  };

  const counts = posts.reduce((acc, p) => {
    const k = p.status || "published";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const filteredPosts = statusFilter === "all" ? posts : posts.filter((p) => (p.status || "published") === statusFilter);
  const nextPublish = posts.find((p) => p.status === "approved") || posts.find((p) => p.status === "pending_approval") || null;

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between gap-6 flex-wrap">
        <div>
          <div className="overline">Blog</div>
          <h1 className="font-serif-display text-5xl text-slate-900">Blog</h1>
          <div className="text-xs text-slate-500 mt-2 max-w-2xl">
            Gerencie posts, aprovações e publicação. Posts pendentes ficam no topo; aprovados vêm antes dos publicados.
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <button onClick={() => setStatusFilter("all")} className={`px-3 py-1 rounded-full border ${statusFilter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>Todos ({posts.length})</button>
            <button onClick={() => setStatusFilter("pending_approval")} className={`px-3 py-1 rounded-full border ${statusFilter === "pending_approval" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-700 border-slate-300"}`}>Pendentes ({counts.pending_approval || 0})</button>
            <button onClick={() => setStatusFilter("approved")} className={`px-3 py-1 rounded-full border ${statusFilter === "approved" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-700 border-slate-300"}`}>Aprovados ({counts.approved || 0})</button>
            <button onClick={() => setStatusFilter("published")} className={`px-3 py-1 rounded-full border ${statusFilter === "published" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300"}`}>Publicado ({counts.published || 0})</button>
          </div>
          {nextPublish && (
            <div className="mt-4 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
              <span className="font-semibold">Próximo da fila:</span> {nextPublish.title} — <span className="font-mono">{nextPublish.status === "approved" ? "apto para publicar" : "pendente de aprovação"}</span>
              {nextPublish.planned_publish_at ? <span className="ml-2 text-sky-700">({nextPublish.planned_publish_at})</span> : null}
            </div>
          )}
        </div>
        <button onClick={() => setEditing({ status: "pending_approval", planned_publish_at: "" })} className="btn-primary" data-testid="blog-new"><Plus size={14} /> Novo post</button>
      </div>

      <div className="mt-10 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="text-left p-3">Título</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Publicação prevista</th>
              <th className="text-left p-3">Slug</th>
              <th className="text-left p-3">Autor</th>
              <th className="text-left p-3">Data</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredPosts.map((p) => (
              <tr key={p.id} className="border-t border-slate-200 align-top">
                <td className="p-3 font-semibold">{p.title}</td>
                <td className="p-3"><BlogStatusBadge status={p.status} /></td>
                <td className="p-3 text-slate-600 text-xs">{p.planned_publish_at || "—"}</td>
                <td className="p-3 text-slate-500 text-xs">{p.slug}</td>
                <td className="p-3 text-slate-600">{p.author}</td>
                <td className="p-3 text-slate-500 text-xs">{p.created_at?.slice(0, 10)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setLocalized(p)} className="text-sky-500 p-1 mr-1" data-testid={`blog-content-${p.slug}`} title="Editar textos localizados"><Globe size={14} /></button>
                  <button onClick={() => setEditing(p)} className="text-slate-600 p-1" data-testid={`blog-edit-${p.slug}`}><Edit size={14} /></button>
                  {p.status === "pending_approval" && (
                    <button
                      onClick={() => runRowAction(p.id, "approve")}
                      disabled={rowActionLoading === `${p.id}:approve`}
                      className="ml-2 rounded border border-slate-300 px-2 py-1 text-xs text-sky-700 hover:bg-sky-50 disabled:opacity-60"
                      data-testid={`blog-approve-row-${p.slug}`}
                    >
                      {rowActionLoading === `${p.id}:approve` ? "Aprovando…" : "Aprovar"}
                    </button>
                  )}
                  {p.status === "approved" && (
                    <button
                      onClick={() => runRowAction(p.id, "publish")}
                      disabled={rowActionLoading === `${p.id}:publish`}
                      className="ml-2 rounded border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                      data-testid={`blog-publish-row-${p.slug}`}
                    >
                      {rowActionLoading === `${p.id}:publish` ? "Publicando…" : "Publicar"}
                    </button>
                  )}
                  <button onClick={() => del(p.id)} className="text-red-500 p-1 ml-2" data-testid={`blog-del-${p.slug}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {filteredPosts.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">Nenhum post nessa visão.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <BlogEditor post={editing} onClose={() => setEditing(null)} onSave={load} />}
      {localized && <BlogContentEditor post={localized} onClose={() => setLocalized(null)} />}
    </div>
  );
}

function BlogEditor({ post, onClose, onSave }) {
  const { authHeaders } = useApp();
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [f, setF] = useState({
    slug: post.slug || "",
    title: post.title || "",
    excerpt: post.excerpt || "",
    cover: post.cover || "",
    body: post.body || "",
    author: post.author || "SharkX",
    planned_publish_at: post.planned_publish_at || "",
  });
  const status = post.status || "pending_approval";

  const save = async () => {
    setSaving(true);
    try {
      if (post.id) await axios.put(`${API}/admin/blog/${post.id}`, f, authHeaders());
      else await axios.post(`${API}/admin/blog`, f, authHeaders());
      onSave();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (action) => {
    if (!post.id) return;
    setActionLoading(action);
    try {
      await axios.post(`${API}/admin/blog/${post.id}/${action}`, {}, authHeaders());
      onSave();
      onClose();
    } finally {
      setActionLoading("");
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-6 overflow-y-auto" data-testid="blog-editor">
      <div className="bg-white rounded-md max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h3 className="font-serif-display text-3xl text-slate-900">{post.id ? "Revisar post" : "Novo post"}</h3>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-slate-500 uppercase tracking-widest">Status atual</span>
              <BlogStatusBadge status={status} />
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost">Fechar</button>
        </div>

        <div className="space-y-3">
          <Field label="Slug" v={f.slug} set={(v) => setF({ ...f, slug: v })} />
          <Field label="Título" v={f.title} set={(v) => setF({ ...f, title: v })} />
          <Field label="Autor" v={f.author} set={(v) => setF({ ...f, author: v })} />
          <Field label="Capa (URL)" v={f.cover} set={(v) => setF({ ...f, cover: v })} />
          <label className="block">
            <div className="text-xs text-slate-500 mb-1 font-ui">Publicação prevista</div>
            <input
              type="datetime-local"
              value={f.planned_publish_at}
              onChange={(e) => setF({ ...f, planned_publish_at: e.target.value })}
              className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm"
              data-testid="blog-planned-publish-at"
            />
          </label>
          {f.cover && <img src={f.cover} alt="preview" className="w-40 h-24 object-cover rounded" />}
          <label className="block">
            <div className="text-xs text-slate-500 mb-1 font-ui">Resumo</div>
            <textarea rows={2} value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm" data-testid="blog-excerpt" />
          </label>
          <label className="block">
            <div className="text-xs text-slate-500 mb-1 font-ui">Corpo (Markdown/texto)</div>
            <textarea rows={12} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm font-mono" data-testid="blog-body" />
          </label>
        </div>
        <div className="mt-6 flex flex-wrap gap-3 justify-end">
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="blog-save"><Save size={14} /> {saving ? "Salvando…" : post.id ? "Salvar revisão" : "Salvar rascunho"}</button>
          <button onClick={() => runAction("approve")} disabled={!post.id || actionLoading} className="btn-ghost border border-slate-300" data-testid="blog-approve"><Globe size={14} /> {actionLoading === "approve" ? "Aprovando…" : "Aprovar Post"}</button>
          <button onClick={() => runAction("publish")} disabled={!post.id || actionLoading} className="btn-ghost border border-emerald-300 text-emerald-700" data-testid="blog-publish"><Save size={14} /> {actionLoading === "publish" ? "Publicando…" : "Publicar agora"}</button>
        </div>
      </div>
    </div>
  );
}

function BlogContentEditor({ post, onClose }) {
  const { authHeaders } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [active, setActive] = useState("pt");
  const [values, setValues] = useState({
    en: { title: "", excerpt: "", body: "" },
    pt: { title: "", excerpt: "", body: "" },
    es: { title: "", excerpt: "", body: "" },
  });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/content`).then((r) => {
      if (!mounted) return;
      const content = r.data || {};
      const next = { en: { title: "", excerpt: "", body: "" }, pt: { title: "", excerpt: "", body: "" }, es: { title: "", excerpt: "", body: "" } };
      ["en", "pt", "es"].forEach((locale) => {
        ["title", "excerpt", "body"].forEach((field) => {
          next[locale][field] = content[`blog.${post.slug}.${field}`]?.[locale]?.text || "";
        });
      });
      setValues(next);
      setLoading(false);
    }).catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [post.slug]);

  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      // Auto-translate blog content only, and only when the translated locale is still empty.
      const nextValues = {
        en: { ...values.en },
        pt: { ...values.pt },
        es: { ...values.es },
      };
      const sourcePT = nextValues.pt;

      const translateFields = async (targetLocale) => {
        const fieldsToTranslate = {};
        ["title", "excerpt", "body"].forEach((field) => {
          if (sourcePT[field] && !nextValues[targetLocale][field]) fieldsToTranslate[field] = sourcePT[field];
        });
        if (!Object.keys(fieldsToTranslate).length) return;

        const resp = await axios.post(
          `${API}/translate/blog`,
          { fields: fieldsToTranslate, source: "pt", target: targetLocale },
          authHeaders(),
        );
        nextValues[targetLocale] = {
          ...nextValues[targetLocale],
          ...resp.data.translations,
        };
      };

      await translateFields("en");
      await translateFields("es");
      setValues(nextValues);

      for (const locale of ["en", "pt", "es"]) {
        for (const field of ["title", "excerpt", "body"]) {
          await axios.post(
            `${API}/admin/content`,
            { key: `blog.${post.slug}.${field}`, locale, value_text: nextValues[locale][field] || "" },
            authHeaders(),
          );
        }
      }

      onClose();
    } catch (e) {
      console.error('Translation/Save error:', e);
      setSaveError(
        e?.response?.data?.detail ||
        'Translation is temporarily unavailable. Nothing was overwritten.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-6 overflow-y-auto" data-testid="blog-content-editor">
      <div className="bg-white rounded-md max-w-5xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-6 mb-6">
          <div>
            <h3 className="font-serif-display text-3xl text-slate-900">Conteúdo localizado</h3>
            <p className="text-slate-500 text-sm mt-2 max-w-2xl">
              Edita as chaves <code className="bg-slate-100 px-1 rounded">blog.{post.slug}.title</code>,
              <code className="bg-slate-100 px-1 rounded ml-1">blog.{post.slug}.excerpt</code> e
              <code className="bg-slate-100 px-1 rounded ml-1">blog.{post.slug}.body</code>.
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost">Fechar</button>
        </div>

        <div className="flex gap-2 mb-6">
          {["pt", "en", "es"].map((l) => (
            <button
              key={l}
              onClick={() => setActive(l)}
              className={`px-3 py-2 rounded text-sm border ${active === l ? "bg-sky-500 text-white border-sky-500" : "bg-white text-slate-600 border-slate-300"}`}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-slate-500">Carregando conteúdo…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
            <div className="space-y-3">
              <Field label="Título" v={values[active].title} set={(v) => setValues({ ...values, [active]: { ...values[active], title: v } })} />
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-ui">Resumo</div>
                <textarea rows={4} value={values[active].excerpt} onChange={(e) => setValues({ ...values, [active]: { ...values[active], excerpt: e.target.value } })} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-ui">Corpo</div>
                <textarea rows={14} value={values[active].body} onChange={(e) => setValues({ ...values, [active]: { ...values[active], body: e.target.value } })} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm font-mono" />
              </label>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-5">
              <div className="overline mb-2">Prévia {active.toUpperCase()}</div>
              <div className="text-sm text-slate-500 mb-3">Slug: <code className="text-slate-900">{post.slug}</code></div>
              <div className="font-serif-display text-3xl text-slate-900 mb-3">{values[active].title || post.title}</div>
              <div className="text-slate-600 text-sm mb-4 whitespace-pre-line">{values[active].excerpt || post.excerpt}</div>
              <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-line max-h-[45vh] overflow-y-auto pr-2">
                {values[active].body || post.body}
              </div>
            </div>
          </div>
        )}

        {saveError && (
          <div className="mt-4 mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 text-sm">
            {saveError}
          </div>
        )}
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={save} disabled={saving || loading} className="btn-primary" data-testid="blog-content-save">
            <Save size={14} /> {saving ? "Salvando…" : "Salvar conteúdo"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, set, type = "text" }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-500 mb-1 font-ui">{label}</div>
      <input type={type} value={v} onChange={(e) => set(e.target.value)} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm" />
    </label>
  );
}
